import React, { useEffect } from 'react';
import styled from 'styled-components';
import { FaChevronLeft, FaChevronRight, FaAngleDoubleLeft, FaAngleDoubleRight } from 'react-icons/fa';
import { PrimaryColor, TextColorLight } from '../styles/global';

const PaginationContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 10px auto;
  width: 100%;
  max-width: 100%;
`;

const ItemsInfo = styled.div`
  font-size: 12px;
  color: ${PrimaryColor};
  text-align: center;
  margin-bottom: 5px;
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1px;
  min-height: 30px;
  flex-wrap: nowrap;
  white-space: nowrap;
  overflow-x: auto;
  width: 100%;
  
  /* Esconde scrollbar mas mantém funcionalidade */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const NavButton = styled.button`
  padding: 0;
  border: none;
  border-radius: 4px;
  background: ${PrimaryColor};
  color: white;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px; /* Reduzido de 32px para 26px */
  height: 32px;
  font-size: 12px;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    opacity: 0.8;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageNumbers = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  margin: 0 4px;
  flex-shrink: 0;
`;

const PageButton = styled.button`
  padding: 0;
  border: none;
  border-radius: 4px;
  background: ${PrimaryColor};
  color: white;
  cursor: ${props => props.active ? 'default' : 'pointer'};
  transition: all 0.2s;
  width: 26px; /* Reduzido de 32px para 26px */
  height: 32px;
  font-weight: ${props => props.active ? 'bold' : 'normal'};
  font-size: 14px;
  opacity: ${props => props.active ? '1' : '0.8'};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    opacity: 1;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 1;
  }
`;

const DotsSpan = styled.span`
  color: ${PrimaryColor};
  font-size: 12px;
  flex-shrink: 0;
  width: 20px; /* Reduzido de 24px para 20px */
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PaginacaoCompleta = ({ 
  totalItens, 
  itemsPerPage = 10, 
  paginaAtual = 1, 
  onPageChange,
  tipo = "itens"
}) => {
  const totalPaginas = Math.ceil(totalItens / itemsPerPage);
  
  // Função para gerar os números das páginas - versão equilibrada
  const gerarNumerosPaginas = () => {
    const rangeWithDots = [];

    // Se há poucas páginas, mostra todas
    if (totalPaginas <= 7) {
      for (let i = 1; i <= totalPaginas; i++) {
        rangeWithDots.push(i);
      }
      return rangeWithDots;
    }

    // Sempre mostra a primeira página
    rangeWithDots.push(1);

    // Se a página atual está longe do início
    if (paginaAtual > 4) {
      rangeWithDots.push('...');
    }

    // Determina o range ao redor da página atual
    const start = Math.max(2, paginaAtual - 1);
    const end = Math.min(totalPaginas - 1, paginaAtual + 1);

    for (let i = start; i <= end; i++) {
      if (!rangeWithDots.includes(i)) {
        rangeWithDots.push(i);
      }
    }

    // Se a página atual está longe do final
    if (paginaAtual < totalPaginas - 3) {
      rangeWithDots.push('...');
    }

    // Sempre mostra a última página
    if (totalPaginas > 1 && !rangeWithDots.includes(totalPaginas)) {
      rangeWithDots.push(totalPaginas);
    }

    return rangeWithDots;
  };

  // Navegação por teclado
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      switch (e.key) {
        case 'ArrowLeft':
          if (paginaAtual > 1) {
            onPageChange(paginaAtual - 1);
          }
          break;
        case 'ArrowRight':
          if (paginaAtual < totalPaginas) {
            onPageChange(paginaAtual + 1);
          }
          break;
        case 'Home':
          onPageChange(1);
          break;
        case 'End':
          onPageChange(totalPaginas);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [paginaAtual, totalPaginas, onPageChange]);

  if (totalPaginas <= 1) return null;

  const inicioItem = (paginaAtual - 1) * itemsPerPage + 1;
  const fimItem = Math.min(paginaAtual * itemsPerPage, totalItens);

  return (
    <PaginationContainer>
      <ItemsInfo>
        Mostrando {inicioItem} a {fimItem} de {totalItens} {tipo}
      </ItemsInfo>

      <PaginationControls>
        {/* Primeira página */}
        <NavButton
          onClick={() => onPageChange(1)}
          disabled={paginaAtual === 1}
          title="Primeira página"
        >
          <FaAngleDoubleLeft />
        </NavButton>

        {/* Página anterior */}
        <NavButton
          onClick={() => onPageChange(paginaAtual - 1)}
          disabled={paginaAtual === 1}
          title="Página anterior"
        >
          <FaChevronLeft />
        </NavButton>

        {/* Números das páginas */}
        {gerarNumerosPaginas().map((numeroPage, index) => {
          if (numeroPage === '...') {
            return (
              <DotsSpan key={`dots-${index}`}>
                ...
              </DotsSpan>
            );
          }

          const ehPaginaAtual = numeroPage === paginaAtual;
          
          return (
            <PageButton
              key={numeroPage}
              onClick={() => onPageChange(numeroPage)}
              disabled={ehPaginaAtual}
              active={ehPaginaAtual}
              title={`Página ${numeroPage}`}
            >
              {numeroPage}
            </PageButton>
          );
        })}

        {/* Próxima página */}
        <NavButton
          onClick={() => onPageChange(paginaAtual + 1)}
          disabled={paginaAtual === totalPaginas}
          title="Próxima página"
        >
          <FaChevronRight />
        </NavButton>

        {/* Última página */}
        <NavButton
          onClick={() => onPageChange(totalPaginas)}
          disabled={paginaAtual === totalPaginas}
          title="Última página"
        >
          <FaAngleDoubleRight />
        </NavButton>
      </PaginationControls>
    </PaginationContainer>
  );
};

export default PaginacaoCompleta; 